import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';

/**
 * Where a part physically lives (INV2-06, SCRUM-268).
 *
 * The maintainer asked for "location codes for inventory that say where
 * the item is such as job sites, warehouses; and each item should have
 * a rack and bin number to quickly identify where the item is."
 *
 * **Two ideas were in that sentence and only one is built.** A location
 * *on each movement* — so the same part can be 300 in Warehouse A and
 * 100 on the Northern job — makes the derived level a quantity per item
 * **per location**, which is a different model for `inventoryLevels`
 * and a different trigger. A **home** location on the item answers
 * "where do I go to find this", which is the question the rack and bin
 * half is about, for a fraction of the work.
 *
 * So the home location is built and the per-location quantities are
 * not. The two are not exclusive — a home location on the item plus
 * quantities per location later is a strictly additive change — and the
 * reasoning is recorded in `docs/features/22-inventory.md` §8b with
 * what it cannot yet say.
 *
 * A **collection rather than free text**, because a filter needs
 * something to group by and a typo must not create a phantom location.
 * The same argument the chart of accounts makes about cost codes.
 */
export const STOCK_LOCATION_KINDS = ['warehouse', 'yard', 'jobSite', 'other'] as const;
export const StockLocationKindSchema = z.enum(STOCK_LOCATION_KINDS);
export type StockLocationKind = z.infer<typeof StockLocationKindSchema>;

export const StockLocationSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  /** "Main warehouse", "Yard", "Saguaro Fuel Mart #12". */
  name: z.string().trim().min(1).max(80),
  kind: StockLocationKindSchema.default('warehouse'),
  /**
   * The job, when this location **is** a job site.
   *
   * Optional and not enforced against `kind`, deliberately: a company
   * that keeps a container on a site before the job exists in the
   * product should still be able to name the place.
   */
  jobId: IdSchema.optional(),
  /**
   * Retired rather than deleted, like a cost code: items point at it,
   * and a deleted location would leave a rack number with no building.
   */
  active: z.boolean().default(true),
});
export type StockLocation = z.infer<typeof StockLocationSchema>;

export const STOCK_LOCATION_KIND_LABEL: Record<StockLocationKind, string> = {
  warehouse: 'Warehouse',
  yard: 'Yard',
  jobSite: 'Job site',
  other: 'Other',
};

/**
 * Where to walk, as one line.
 *
 * In core rather than in a component because three screens say it — the
 * list, the item dialog and the custody view that follows — and three
 * copies is three chances for one to leave the bin off.
 */
export function whereToFind(
  location: Pick<StockLocation, 'name'> | null | undefined,
  item: { readonly rack?: string; readonly bin?: string },
): string {
  /**
   * **Trimmed before it is tested, not after.** A rack of `"  "` is
   * truthy, so the first version produced "rack" with nothing after it
   * — a label for a value nobody recorded. Its own test caught it.
   */
  const rack = item.rack?.trim();
  const bin = item.bin?.trim();
  const parts = [
    location?.name?.trim(),
    rack ? `rack ${rack}` : '',
    bin ? `bin ${bin}` : '',
  ].filter(Boolean);
  // Nothing recorded reads as nothing, not as an empty string somebody
  // mistakes for a blank cell they could type into.
  return parts.length === 0 ? '' : parts.join(' · ');
}
